import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useSubscription } from '../SubscriptionContext';
import { useAuth } from '../AuthContext';
import Paywall from './Paywall';
import AppLoading from './AppLoading';
import { releaseSplash } from '../utils/splash';

/* Routes that must remain reachable even when the user has no subscription —
   they can still sign in/out and read legal pages. */
const PASSTHROUGH_PATHS = ['/login', '/legal', '/terms', '/privacy', '/support'];

/* Wraps any tree that requires an active subscription on native apps.
   Web users pass through unchanged (no IAP API in browsers).
   Sys-admins and admin-set Pro users also pass through (handled in
   SubscriptionContext via isBypassed). */
export default function SubscriptionGate({ children }) {
  const { currentUser, authInitialized } = useAuth();
  const { isReady, isEntitled, isBypassed, welcome } = useSubscription();
  const location = useLocation();

  // The native splash is held until this gate knows which screen it is about
  // to show (utils/splash.js): Firebase has said who is signed in (with a
  // cached profile this gate mounts BEFORE it has, with no user yet), and for
  // a signed-in phone user who is not bypassed, RevenueCat has answered.
  const settled = authInitialized
    && (!currentUser || !Capacitor.isNativePlatform() || isBypassed || isReady);
  useEffect(() => { if (settled) releaseSplash(); }, [settled]);

  // Every path that shows the app returns this ONE shape — children first,
  // then an optional overlay — so moving between them (login → Home, or the
  // paywall's "You're in" fading off) never remounts the app beneath.
  const app = (overlay = null) => <>{children}{overlay}</>;

  // Always allow login / legal / support pages to render through the gate.
  if (PASSTHROUGH_PATHS.some(p => location.pathname.startsWith(p))) {
    return app();
  }

  // Not signed in yet — the auth gate will handle that elsewhere.
  if (!currentUser) return app();

  // No paywall on web.
  if (!Capacitor.isNativePlatform()) return app();

  // Users who bypass the paywall (sys-admins, admin-set Pro) shouldn't wait
  // for the RC SDK to initialise at all.
  if (isBypassed) return app();

  // While the RC SDK is initialising, the shared loading screen — the same
  // navy as the splash and the auth wait, so the path into the paywall is one
  // surface rather than three. It also avoids a flash of paywall before the
  // real entitlement check completes.
  if (!isReady) return <AppLoading />;

  if (!isEntitled && !welcome) return <Paywall />;

  // Just paid (or restored): Home mounts UNDERNEATH while the paywall shows
  // "You're in" and fades away over it, so the reveal lands on a page that has
  // already started loading rather than cutting to one that hasn't. The SAME
  // shape with and without the overlay, on purpose — returning bare `children`
  // once it has gone would remount Home at the very moment it is revealed.
  return app(welcome ? <Paywall /> : null);
}
